import React, { useState, useEffect, useCallback, useRef } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Avatar from '@mui/material/Avatar';
import Divider from '@mui/material/Divider';
import Collapse from '@mui/material/Collapse';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import CircularProgress from '@mui/material/CircularProgress';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Tooltip from '@mui/material/Tooltip';
import Chip from '@mui/material/Chip';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Popper from '@mui/material/Popper';
import SendIcon from '@mui/icons-material/Send';
import DeleteIcon from '@mui/icons-material/Delete';
import ReplyIcon from '@mui/icons-material/Reply';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloudIcon from '@mui/icons-material/Cloud';
import SettingsIcon from '@mui/icons-material/Settings';
import { formatDistanceToNow } from 'date-fns';
import { useSession } from '../../context/SessionContext';
import { getJiraComments, postJiraComment } from '../../utils/api';

const ItemComments = ({
  itemId,
  comments: initialComments = [],
  onAddComment,
  onDeleteComment,
  jiraKey = null,
  jiraUrl = null,
  isFacilitator = false,
  participants = []
}) => {
  const { participant, socket } = useSession();
  const [comments, setComments] = useState(initialComments);
  const [jiraComments, setJiraComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [replyTo, setReplyTo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [jiraLoading, setJiraLoading] = useState(false);
  const [expanded, setExpanded] = useState(true);
  const [postToJira, setPostToJira] = useState(false);
  const [jiraError, setJiraError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  // Mention autocomplete state
  const [mentionAnchor, setMentionAnchor] = useState(null);
  const [mentionSearch, setMentionSearch] = useState('');
  const [mentionStartIndex, setMentionStartIndex] = useState(null);
  const textFieldRef = useRef(null);

  // Filter participants for mention suggestions
  const filteredParticipants = participants.filter(p =>
    p.name?.toLowerCase().includes(mentionSearch.toLowerCase())
  );

  // Handle text input changes with mention detection
  const handleCommentChange = (e) => {
    const value = e.target.value;
    const cursorPos = e.target.selectionStart;
    setNewComment(value);

    // Find the last @ before cursor
    const textBeforeCursor = value.substring(0, cursorPos);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');

    if (lastAtIndex !== -1) {
      // Check if @ is at start or preceded by whitespace
      const charBeforeAt = lastAtIndex > 0 ? value[lastAtIndex - 1] : ' ';
      if (charBeforeAt === ' ' || charBeforeAt === '\n' || lastAtIndex === 0) {
        const searchText = textBeforeCursor.substring(lastAtIndex + 1);
        // Only show autocomplete if there's no space in the search (incomplete mention)
        if (!searchText.includes(' ')) {
          setMentionSearch(searchText);
          setMentionStartIndex(lastAtIndex);
          setMentionAnchor(textFieldRef.current);
          return;
        }
      }
    }

    // Close autocomplete if no valid @ found
    setMentionAnchor(null);
    setMentionSearch('');
    setMentionStartIndex(null);
  };

  // Insert mention into comment
  const handleSelectMention = (selectedParticipant) => {
    if (mentionStartIndex !== null) {
      const beforeMention = newComment.substring(0, mentionStartIndex);
      const afterMention = newComment.substring(mentionStartIndex + 1 + mentionSearch.length);
      const newText = `${beforeMention}@${selectedParticipant.name} ${afterMention}`;
      setNewComment(newText);
    }
    setMentionAnchor(null);
    setMentionSearch('');
    setMentionStartIndex(null);
    // Focus back on the text field
    if (textFieldRef.current) {
      textFieldRef.current.focus();
    }
  };

  // Render comment content with highlighted mentions
  const renderCommentWithMentions = (content) => {
    if (!content) return null;

    // Match @Name patterns (name can contain letters, spaces, numbers, hyphens)
    const mentionRegex = /@([A-Za-z][A-Za-z0-9 -]*?)(?=\s|$|[.,!?])/g;
    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = mentionRegex.exec(content)) !== null) {
      // Add text before mention
      if (match.index > lastIndex) {
        parts.push(content.substring(lastIndex, match.index));
      }
      // Add highlighted mention
      const mentionName = match[1].trim();
      const isMentionedParticipant = participants.some(
        p => p.name?.toLowerCase() === mentionName.toLowerCase()
      );
      parts.push(
        <Box
          component="span"
          key={match.index}
          sx={{
            bgcolor: isMentionedParticipant ? 'primary.main' : 'action.selected',
            color: isMentionedParticipant ? 'primary.contrastText' : 'text.primary',
            borderRadius: 0.5,
            px: 0.5,
            fontWeight: 500
          }}
        >
          @{mentionName}
        </Box>
      );
      lastIndex = match.index + match[0].length;
    }
    // Add remaining text
    if (lastIndex < content.length) {
      parts.push(content.substring(lastIndex));
    }

    return parts.length > 0 ? parts : content;
  };

  // Update comments when initialComments change
  useEffect(() => {
    setComments(initialComments);
  }, [initialComments]);

  // Fetch Jira comments when jiraKey changes or item becomes active
  const fetchJiraComments = useCallback(async () => {
    if (!jiraKey || !isFacilitator) return;

    setJiraLoading(true);
    setJiraError(null);

    try {
      const response = await getJiraComments(jiraKey);
      setJiraComments(response.comments || []);
    } catch (error) {
      console.error('Error fetching Jira comments:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch Jira comments';
      setJiraError(errorMessage);
      // Don't show snackbar for auth errors - user may not have Jira configured
      if (error.response?.status !== 400) {
        setSnackbar({
          open: true,
          message: errorMessage,
          severity: 'error'
        });
      }
    } finally {
      setJiraLoading(false);
    }
  }, [jiraKey, isFacilitator]);

  // Fetch Jira comments on mount and when jiraKey changes
  useEffect(() => {
    if (jiraKey && isFacilitator) {
      fetchJiraComments();
    }
  }, [jiraKey, isFacilitator, fetchJiraComments]);

  // Auto-check "Post to Jira" when Jira is available
  useEffect(() => {
    if (jiraKey && isFacilitator) {
      setPostToJira(true);
    }
  }, [jiraKey, isFacilitator]);

  // Listen for comment updates via socket
  useEffect(() => {
    if (!socket) return;

    const handleCommentAdded = (data) => {
      if (data.itemId === itemId) {
        setComments(prev => {
          // Check if it's a reply
          if (data.comment.parentCommentId) {
            return prev.map(c =>
              c.id === data.comment.parentCommentId
                ? { ...c, replies: [...(c.replies || []), data.comment] }
                : c
            );
          }
          // Check if comment already exists
          const exists = prev.some(c => c.id === data.comment.id);
          if (exists) return prev;
          return [...prev, data.comment];
        });
      }
    };

    const handleCommentDeleted = (data) => {
      if (data.itemId === itemId) {
        setComments(prev => {
          // Check if it's a parent comment
          const filteredComments = prev.filter(c => c.id !== data.commentId);
          // Also filter from replies
          return filteredComments.map(c => ({
            ...c,
            replies: (c.replies || []).filter(r => r.id !== data.commentId)
          }));
        });
      }
    };

    socket.on('comment-added', handleCommentAdded);
    socket.on('comment-deleted', handleCommentDeleted);

    return () => {
      socket.off('comment-added', handleCommentAdded);
      socket.off('comment-deleted', handleCommentDeleted);
    };
  }, [socket, itemId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    setLoading(true);
    let jiraPostSucceeded = false;

    try {
      // Post to Jira if checkbox is checked and we have a jiraKey
      if (postToJira && jiraKey && isFacilitator) {
        try {
          await postJiraComment(jiraKey, newComment.trim());
          jiraPostSucceeded = true;
          setSnackbar({
            open: true,
            message: 'Comment posted to Jira successfully',
            severity: 'success'
          });
          // Refresh Jira comments to show the new one
          fetchJiraComments();
        } catch (jiraErr) {
          console.error('Error posting to Jira:', jiraErr);
          setSnackbar({
            open: true,
            message: jiraErr.response?.data?.message || 'Failed to post comment to Jira. Saving locally instead.',
            severity: 'warning'
          });
        }
      }

      // Only add local comment if NOT posting to Jira, or if Jira post failed
      if (!postToJira || !jiraPostSucceeded) {
        if (socket) {
          socket.emit('add-comment', {
            itemId,
            content: newComment.trim(),
            parentCommentId: replyTo,
            jiraSyncStatus: 'not_synced'
          });
        }

        if (onAddComment) {
          await onAddComment(itemId, newComment.trim(), replyTo);
        }
      }

      setNewComment('');
      setReplyTo(null);
      // Keep postToJira checked if Jira is configured, otherwise reset
      if (!jiraKey || !isFacilitator) {
        setPostToJira(false);
      }
    } catch (error) {
      console.error('Error adding comment:', error);
      setSnackbar({
        open: true,
        message: 'Failed to add comment',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (commentId) => {
    if (socket) {
      socket.emit('delete-comment', { commentId });
    }
    if (onDeleteComment) {
      onDeleteComment(commentId);
    }
  };

  const canDelete = (comment) => {
    if (!participant) return false;
    return (
      comment.participantId === participant.id ||
      participant.role === 'facilitator'
    );
  };

  // Merge and sort all comments
  const getAllComments = () => {
    // Convert Jira comments to unified format
    const formattedJiraComments = jiraComments.map(jc => ({
      id: `jira-${jc.id}`,
      content: jc.body,
      createdAt: jc.created,
      source: 'jira',
      jiraAuthor: jc.author,
      participant: { name: jc.author, role: 'external' }
    }));

    // Add source field to local comments
    const formattedLocalComments = comments.map(c => ({
      ...c,
      source: c.source || 'local'
    }));

    // Combine and sort by date
    const allComments = [...formattedLocalComments, ...formattedJiraComments];
    allComments.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));

    return allComments;
  };

  const CommentItem = ({ comment, isReply = false }) => {
    const isJiraComment = comment.source === 'jira';
    const isSynced = comment.jiraSyncStatus === 'synced';

    return (
      <ListItem
        alignItems="flex-start"
        sx={{
          py: 1,
          px: isReply ? 2 : 0,
          pl: isReply ? 4 : 0,
          borderLeft: isReply ? '2px solid' : 'none',
          borderColor: 'divider'
        }}
      >
        <Box sx={{ display: 'flex', width: '100%', gap: 1 }}>
          <Avatar
            sx={{
              width: isReply ? 24 : 32,
              height: isReply ? 24 : 32,
              fontSize: isReply ? '0.75rem' : '0.875rem',
              bgcolor: isJiraComment
                ? 'info.main'
                : comment.participant?.role === 'facilitator'
                  ? 'primary.main'
                  : 'secondary.main'
            }}
          >
            {isJiraComment
              ? <CloudIcon fontSize="small" />
              : comment.participant?.name?.charAt(0).toUpperCase() || '?'}
          </Avatar>

          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="subtitle2" component="span">
                  {comment.participant?.name || comment.jiraAuthor || 'Unknown'}
                </Typography>
                {isJiraComment && (
                  <Chip
                    icon={<CloudIcon sx={{ fontSize: '0.875rem !important' }} />}
                    label="Jira"
                    size="small"
                    color="info"
                    variant="outlined"
                    sx={{ height: 20, '& .MuiChip-label': { px: 0.5, fontSize: '0.7rem' } }}
                  />
                )}
                {!isJiraComment && isSynced && (
                  <Tooltip title="Synced to Jira">
                    <CheckCircleIcon sx={{ fontSize: '1rem', color: 'success.main' }} />
                  </Tooltip>
                )}
                {comment.participant?.role === 'facilitator' && !isJiraComment && (
                  <Typography
                    component="span"
                    variant="caption"
                    sx={{ ml: 0.5, color: 'primary.main' }}
                  >
                    Facilitator
                  </Typography>
                )}
              </Box>
              <Typography variant="caption" color="text.secondary">
                {formatDistanceToNow(new Date(comment.createdAt), { addSuffix: true })}
              </Typography>
            </Box>

            <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
              {renderCommentWithMentions(comment.content)}
            </Typography>

            <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
              {!isReply && !isJiraComment && (
                <Button
                  size="small"
                  startIcon={<ReplyIcon fontSize="small" />}
                  onClick={() => setReplyTo(comment.id)}
                  sx={{ fontSize: '0.75rem' }}
                >
                  Reply
                </Button>
              )}
              {canDelete(comment) && !isJiraComment && (
                <IconButton
                  size="small"
                  onClick={() => handleDelete(comment.id)}
                  sx={{ color: 'text.secondary' }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          </Box>
        </Box>
      </ListItem>
    );
  };

  const allComments = getAllComments();
  const totalCount = allComments.length;

  return (
    <Paper variant="outlined" sx={{ mt: 2 }}>
      <Box
        sx={{
          p: 1.5,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          cursor: 'pointer',
          '&:hover': { bgcolor: 'action.hover' }
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="subtitle2">
            Comments ({totalCount})
          </Typography>
          {jiraKey && isFacilitator && (
            <Chip
              icon={<CloudIcon sx={{ fontSize: '0.875rem !important' }} />}
              label="Jira"
              size="small"
              color="info"
              variant="outlined"
              sx={{ height: 20, '& .MuiChip-label': { px: 0.5, fontSize: '0.7rem' } }}
            />
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {jiraKey && isFacilitator && (
            <Tooltip title="Refresh Jira comments">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  fetchJiraComments();
                }}
                disabled={jiraLoading}
              >
                {jiraLoading ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          )}
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </Box>
      </Box>

      <Collapse in={expanded}>
        <Divider />

        {/* No Jira configured message for facilitators */}
        {!jiraKey && isFacilitator && (
          <Box sx={{ px: 2, py: 1, bgcolor: 'action.hover' }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <SettingsIcon sx={{ fontSize: '0.875rem' }} />
              This item has no Jira link. Import from Jira to see linked issue comments.
            </Typography>
          </Box>
        )}

        {/* Jira error message */}
        {jiraError && jiraKey && (
          <Box sx={{ px: 2, py: 1, bgcolor: 'rgba(245, 158, 11, 0.1)' }}>
            <Typography variant="caption" color="warning.main">
              Could not load Jira comments: {jiraError}
            </Typography>
          </Box>
        )}

        {allComments.length > 0 ? (
          <List sx={{ p: 1 }}>
            {allComments.map((comment) => (
              <React.Fragment key={comment.id}>
                <CommentItem comment={comment} />
                {comment.replies && comment.replies.length > 0 && (
                  <List disablePadding>
                    {comment.replies.map((reply) => (
                      <CommentItem key={reply.id} comment={reply} isReply />
                    ))}
                  </List>
                )}
              </React.Fragment>
            ))}
          </List>
        ) : (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No comments yet. Be the first to comment!
            </Typography>
          </Box>
        )}

        <Divider />

        <Box component="form" onSubmit={handleSubmit} sx={{ p: 1.5 }}>
          {replyTo && (
            <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Replying to comment
              </Typography>
              <Button size="small" onClick={() => setReplyTo(null)}>
                Cancel
              </Button>
            </Box>
          )}

          <Box sx={{ display: 'flex', gap: 1, position: 'relative' }}>
            <TextField
              size="small"
              fullWidth
              placeholder={replyTo ? 'Write a reply...' : participants.length > 0 ? 'Write a comment... (use @ to mention)' : 'Write a comment...'}
              value={newComment}
              onChange={handleCommentChange}
              disabled={loading}
              multiline
              maxRows={3}
              inputRef={textFieldRef}
            />
            <Button
              type="submit"
              variant="contained"
              disabled={loading || !newComment.trim()}
              sx={{ minWidth: 'auto', px: 2 }}
            >
              {loading ? <CircularProgress size={20} /> : <SendIcon />}
            </Button>
          </Box>

          {/* Post to Jira checkbox - only show for facilitator with jiraKey */}
          {jiraKey && isFacilitator && !replyTo && (
            <Box sx={{ mt: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={postToJira}
                    onChange={(e) => setPostToJira(e.target.checked)}
                    disabled={loading}
                  />
                }
                label={
                  <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <CloudIcon sx={{ fontSize: '0.875rem' }} />
                    Also post to Jira
                  </Typography>
                }
              />
            </Box>
          )}

          {/* Mention autocomplete dropdown */}
          <Popper
            open={Boolean(mentionAnchor) && filteredParticipants.length > 0}
            anchorEl={mentionAnchor}
            placement="top-start"
            sx={{ zIndex: 1300 }}
          >
            <Paper
              elevation={8}
              sx={{
                maxHeight: 200,
                overflow: 'auto',
                minWidth: 200,
                mb: 1
              }}
            >
              <List dense disablePadding>
                {filteredParticipants.map((p) => (
                  <ListItemButton
                    key={p.id}
                    onClick={() => handleSelectMention(p)}
                    sx={{ py: 0.5 }}
                  >
                    <ListItemAvatar sx={{ minWidth: 36 }}>
                      <Avatar
                        sx={{
                          width: 24,
                          height: 24,
                          fontSize: '0.75rem',
                          bgcolor: p.role === 'facilitator' ? 'primary.main' : 'secondary.main'
                        }}
                      >
                        {p.name?.charAt(0).toUpperCase() || '?'}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={p.name}
                      secondary={p.role === 'facilitator' ? 'Facilitator' : 'Participant'}
                      primaryTypographyProps={{ variant: 'body2' }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItemButton>
                ))}
              </List>
            </Paper>
          </Popper>
        </Box>
      </Collapse>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default ItemComments;
